import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import TipCard from "@/components/feature/TipCard";
import { categories } from "@/mocks/categories";
import { tips } from "@/mocks/tips";
import type { Tip } from "@/types/content";

const tipList: Tip[] = tips;
const PAGE_SIZE = 6;

const sortOptions = [
  { key: "latest", label: "最新发布", icon: "ri-time-line" },
  { key: "hot", label: "最多阅读", icon: "ri-fire-line" },
  { key: "liked", label: "最多点赞", icon: "ri-heart-3-line" },
] as const;

type SortKey = (typeof sortOptions)[number]["key"];

export default function Feed() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const category = searchParams.get("category") ?? "all";
  const pageParam = Number(searchParams.get("page") ?? "1") || 1;

  const [keyword, setKeyword] = useState(q);
  const [sort, setSort] = useState<SortKey>("latest");

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const updateParams = (next: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (value === null || value === "") {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    setSearchParams(params);
  };

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    let list = tipList.filter((tip) => category === "all" || tip.category === category);
    if (kw) {
      list = list.filter(
        (tip) =>
          tip.title.toLowerCase().includes(kw) ||
          tip.summary.toLowerCase().includes(kw) ||
          tip.location.toLowerCase().includes(kw) ||
          tip.tags.some((tag) => tag.toLowerCase().includes(kw)),
      );
    }
    if (sort === "hot") {
      list = [...list].sort((a, b) => b.views - a.views);
    } else if (sort === "liked") {
      list = [...list].sort((a, b) => b.likes - a.likes);
    } else {
      list = [...list].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    }
    return list;
  }, [q, category, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, pageParam), totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const activeCategory = categories.find((item) => item.slug === category);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParams({ q: keyword.trim() || null, page: null });
  };

  return (
    <>
      <SeoHead
        title="爆料广场 - 分类浏览最新爆料与热点线索 | 爆料专区"
        description="爆料广场汇集城市身边事、消费观察、职场见闻、校园故事、生活发现与热点追踪六大分类的最新爆料，支持关键词搜索与排序筛选，并可进入评论区参与互动留言。"
        keywords="爆料广场,最新爆料,爆料分类,热点线索,评论区互动,爆料专区"
        path="/feed"
      />

      <div className="bg-background-50">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "爆料广场" }]} />

          <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-display text-3xl text-foreground-950 md:text-4xl">爆料广场</h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {activeCategory
                  ? `正在浏览「${activeCategory.label}」——${activeCategory.desc}`
                  : "按主题分类浏览所有投稿，用关键词找到你关心的那条线索，再进评论区看看大家怎么说。"}
              </p>
            </div>
            <p className="font-label text-sm text-foreground-500">
              共 <span className="font-display text-lg text-primary-700">{filtered.length}</span> 条爆料
            </p>
          </div>

          <div className="mt-7 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
            <form onSubmit={handleSearch} role="search" className="flex flex-col gap-2 sm:flex-row">
              <label htmlFor="feed-search" className="sr-only">
                搜索爆料
              </label>
              <div className="relative flex-1">
                <i
                  className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                  aria-hidden="true"
                />
                <input
                  id="feed-search"
                  name="q"
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="输入关键词，例如：食堂、价签、自习室"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
              {q ? (
                <button
                  type="button"
                  onClick={() => updateParams({ q: null, page: null })}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 font-label text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  清空
                </button>
              ) : null}
            </form>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => updateParams({ category: null, page: null })}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-sm transition-colors ${
                  category === "all"
                    ? "bg-secondary-700 text-background-50"
                    : "bg-background-200 text-foreground-700 hover:bg-secondary-200"
                }`}
              >
                全部分类
              </button>
              {categories.map((item) => (
                <button
                  key={item.slug}
                  type="button"
                  onClick={() => updateParams({ category: item.slug, page: null })}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-sm transition-colors ${
                    category === item.slug
                      ? "bg-secondary-700 text-background-50"
                      : "bg-background-200 text-foreground-700 hover:bg-secondary-200"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
              <span className="mr-1 font-label text-xs text-foreground-500">排序：</span>
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-sm transition-colors ${
                    sort === option.key
                      ? "bg-accent-500 text-foreground-950"
                      : "bg-background-200 text-foreground-700 hover:bg-accent-100"
                  }`}
                >
                  <i className={option.icon} aria-hidden="true" />
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-background-100 pb-16 pt-8 md:pb-24">
        <div className="mx-auto w-full px-4 md:px-8">
          {paged.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {paged.map((tip) => (
                <TipCard key={tip.id} tip={tip} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl" aria-hidden="true" />
              </span>
              <h2 className="mt-4 font-display text-lg text-foreground-950">没有找到相关爆料</h2>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                换个关键词试试，或者直接把你知道的写下来，成为这个分类里的第一条。
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/submit"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  我要爆料
                </Link>
                <button
                  type="button"
                  onClick={() => updateParams({ q: null, category: null, page: null })}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  重置筛选
                </button>
              </div>
            </div>
          )}

          {totalPages > 1 ? (
            <nav aria-label="分页导航" className="mt-12 flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => updateParams({ page: String(currentPage - 1) })}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i className="ri-arrow-left-s-line" aria-hidden="true" />
                上一页
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => updateParams({ page: num === 1 ? null : String(num) })}
                  aria-current={num === currentPage ? "page" : undefined}
                  className={`h-10 w-10 rounded-md font-label text-sm transition-colors ${
                    num === currentPage
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {num}
                </button>
              ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => updateParams({ page: String(currentPage + 1) })}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                下一页
                <i className="ri-arrow-right-s-line" aria-hidden="true" />
              </button>
            </nav>
          ) : null}
        </div>
      </div>
    </>
  );
}